import React from 'react';
import {Stack} from '@mui/material';
import {COLORS} from '~/style/colors-ts';

export type IconWrapperColor = 'default' | 'error' | 'success' | 'warning' | 'info' | 'primary' | 'secondary';
export type IconWrapperSize = 'small' | 'medium';

export type IconWrapperProps = {
    children: React.ReactElement;
    color?: IconWrapperColor;
    size?: IconWrapperSize;
    /** When true, shows the shade-100 background. Defaults to true. */
    bg?: boolean;
    className?: string;
};

const COLOR_TOKENS: Record<IconWrapperColor, {main: string; bg: string}> = {
    default: {main: COLORS.black[500], bg: COLORS.grey[100]},
    error: {main: COLORS.error.main, bg: COLORS.error[100]},
    success: {main: COLORS.success.main, bg: COLORS.success[100]},
    warning: {main: COLORS.warning.main, bg: COLORS.warning[100]},
    // info has no 100 token; reuse the matching blue tint
    info: {main: COLORS.info.main, bg: COLORS.mithraBlue[100]},
    primary: {main: COLORS.primary.main, bg: COLORS.primary[100]},
    secondary: {main: COLORS.secondary.main, bg: COLORS.secondary[100]},
};

const SIZE_TOKENS: Record<IconWrapperSize, {box: number; icon: number}> = {
    medium: {box: 32, icon: 16},
    small: {box: 22, icon: 14},
};

export function IconWrapper({
                                children,
                                color = 'default',
                                size = 'medium',
                                bg = true,
                                className,
                            }: IconWrapperProps) {
    const tokens = COLOR_TOKENS[color];
    const dimensions = SIZE_TOKENS[size];

    return (
        <Stack
            className={className}
            justifyContent="center"
            alignItems="center"
            sx={{
                width: bg? dimensions.box : dimensions.icon,
                height: bg? dimensions.box : dimensions.icon,
                flexShrink: 0,
                borderRadius: 1,
                color: color==='default' && !bg? COLORS.grey[500]: tokens.main,
                bgcolor: bg ? tokens.bg : 'transparent',
            }}
        >
            {React.cloneElement(children, {
                style: {
                    width: dimensions.icon,
                    height: dimensions.icon,
                    ...children.props.style,
                },
            })}
        </Stack>
    );
}
